import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button, Stack, Avatar,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Alert,
  Tabs, Tab, Stepper, Step, StepLabel, Radio, RadioGroup, FormControlLabel,
  FormControl, FormLabel, LinearProgress, Divider,
} from '@mui/material';
import { Group, Add, Handshake, Person, School, Quiz, Star, CheckCircle } from '@mui/icons-material';
import { bookingsAPI, pairingQuizAPI } from '../../api/client';
import useAuthStore from '../../store/useStore';

const QUIZ_QUESTIONS = [
  { field: 'sleep_schedule', label: 'What is your sleep schedule?', options: [
    { value: 'early_bird', label: 'Early Bird (before 10pm)' },
    { value: 'night_owl', label: 'Night Owl (after midnight)' },
    { value: 'flexible', label: 'Flexible / Varies' },
  ]},
  { field: 'noise_level', label: 'What noise level do you prefer?', options: [
    { value: 'quiet', label: 'Quiet - I need silence to focus' },
    { value: 'moderate', label: 'Moderate - some background noise is fine' },
    { value: 'loud', label: 'Loud - I like lively environments' },
  ]},
  { field: 'cleanliness', label: 'How would you describe your cleanliness?', options: [
    { value: 'very_clean', label: 'Very clean - I tidy up daily' },
    { value: 'average', label: 'Average - clean regularly' },
    { value: 'relaxed', label: 'Relaxed - I clean when needed' },
  ]},
  { field: 'guests_frequency', label: 'How often do you have guests over?', options: [
    { value: 'never', label: 'Rarely or never' },
    { value: 'sometimes', label: 'Sometimes (weekly)' },
    { value: 'often', label: 'Often (several times a week)' },
  ]},
  { field: 'study_habits', label: 'Where do you prefer to study?', options: [
    { value: 'in_room', label: 'In my room' },
    { value: 'library', label: 'Library or study space' },
    { value: 'both', label: 'Both / It depends' },
  ]},
  { field: 'smoking', label: 'What is your smoking preference?', options: [
    { value: 'no', label: 'Non-smoker' },
    { value: 'outside_only', label: 'Only smoke outside' },
    { value: 'yes', label: 'Smoker' },
  ]},
  { field: 'pets', label: 'How do you feel about pets?', options: [
    { value: 'no_pets', label: 'No pets please' },
    { value: 'okay_with_pets', label: 'Okay with pets' },
    { value: 'has_pets', label: 'I have a pet' },
  ]},
  { field: 'music_preference', label: 'How do you listen to music?', options: [
    { value: 'headphones', label: 'Always with headphones' },
    { value: 'speakers_low', label: 'Speakers at low volume' },
    { value: 'speakers_loud', label: 'Speakers at full blast' },
  ]},
  { field: 'sharing_items', label: 'How do you feel about sharing items?', options: [
    { value: 'not_comfortable', label: 'I prefer not to share' },
    { value: 'some_items', label: 'Some items are fine' },
    { value: 'open_to_sharing', label: 'Open to sharing most things' },
  ]},
  { field: 'conflict_resolution', label: 'How do you prefer to resolve conflicts?', options: [
    { value: 'direct_conversation', label: 'Direct conversation' },
    { value: 'mediator', label: 'Through a mediator' },
    { value: 'written', label: 'Written communication' },
  ]},
  { field: 'budget_range', label: 'What is your budget range?', options: [
    { value: 'low', label: 'Budget-friendly / Low cost' },
    { value: 'medium', label: 'Mid-range' },
    { value: 'high', label: 'Premium / Higher budget' },
  ]},
  { field: 'gender_preference', label: 'Roommate gender preference?', options: [
    { value: 'same_gender', label: 'Same gender only' },
    { value: 'any_gender', label: 'Any gender is fine' },
    { value: 'no_preference', label: 'No preference' },
  ]},
];

function RoomPairingPage() {
  const { user } = useAuthStore();
  const [tabValue, setTabValue] = useState(0);
  const [pairings, setPairings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ property_id: '', message: '', preferences: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Quiz state
  const [quizStep, setQuizStep] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizBio, setQuizBio] = useState('');
  const [quizProfile, setQuizProfile] = useState(null);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizLoading, setQuizLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);

  const fetchPairings = async () => {
    setLoading(true);
    try {
      const res = await bookingsAPI.getAvailablePairings({});
      setPairings(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchQuizProfile = async () => {
    try {
      const res = await pairingQuizAPI.getMyProfile();
      if (res.data?.completed) {
        setQuizProfile(res.data.profile);
        setQuizCompleted(true);
      }
    } catch (_) {}
  };

  const fetchMatches = async () => {
    setMatchesLoading(true);
    try {
      const res = await pairingQuizAPI.getMatches();
      setMatches(res.data || []);
    } catch (_) {}
    finally { setMatchesLoading(false); }
  };

  useEffect(() => { fetchPairings(); fetchQuizProfile(); }, []);
  useEffect(() => { if (quizCompleted) fetchMatches(); }, [quizCompleted]);

  const handleQuizAnswer = (field, value) => {
    setQuizAnswers(prev => ({ ...prev, [field]: value }));
  };

  const handleQuizSubmit = async () => {
    setQuizLoading(true); setError('');
    try {
      const data = { ...quizAnswers, bio: quizBio };
      const res = await pairingQuizAPI.submitQuiz(data);
      setQuizProfile(res.data);
      setQuizCompleted(true);
      setSuccess('Roommate profile created! Check your compatibility matches.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit quiz');
    } finally { setQuizLoading(false); }
  };

  const handleCreate = async () => {
    setError('');
    try {
      const data = { message: createForm.message || null, preferences: createForm.preferences || null };
      if (createForm.property_id && createForm.property_id.trim() !== '') data.property_id = createForm.property_id;
      await bookingsAPI.createPairing(data);
      setCreateOpen(false);
      setCreateForm({ property_id: '', message: '', preferences: '' });
      setSuccess('Pairing request created!');
      fetchPairings();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Failed to create pairing request');
    }
  };

  const handleMatch = async (pairingId) => {
    setError(''); setSuccess('');
    try {
      await bookingsAPI.matchPairing(pairingId);
      setSuccess('Successfully matched! You can now coordinate with your roommate.');
      fetchPairings();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to match');
    }
  };

  const currentQ = QUIZ_QUESTIONS[quizStep];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Room Pairing</Typography>
          <Typography variant="body2" color="text.secondary">Find a compatible roommate to share accommodation</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>Post Pairing Request</Button>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ borderRadius: 3, mb: 3, px: 1 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ '& .MuiTabs-indicator': { height: 3, borderRadius: 2 } }}>
          <Tab label="Roommate Quiz" icon={<Quiz sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="My Matches" icon={<Star sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Pairing Requests" icon={<Group sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Roommate Quiz */}
      {tabValue === 0 && (
        <>
          {quizCompleted && quizProfile ? (
            <Paper sx={{ p: 4, borderRadius: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}><CheckCircle sx={{ fontSize: 32 }} /></Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>Your Roommate Profile</Typography>
                  <Typography color="text.secondary">Your quiz is complete! Here's your profile summary.</Typography>
                </Box>
              </Stack>
              <Grid container spacing={2}>
                {QUIZ_QUESTIONS.map(q => {
                  const val = quizProfile[q.field];
                  const opt = q.options.find(o => o.value === val);
                  return val ? (
                    <Grid item xs={12} sm={6} md={4} key={q.field}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">{q.label}</Typography>
                        <Typography fontWeight={600}>{opt?.label || val.replace(/_/g, ' ')}</Typography>
                      </Paper>
                    </Grid>
                  ) : null;
                })}
              </Grid>
              {quizProfile.bio && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">About Me</Typography>
                  <Typography>{quizProfile.bio}</Typography>
                </Paper>
              )}
              <Button variant="outlined" sx={{ mt: 3 }} onClick={() => { setQuizCompleted(false); setQuizStep(0); setQuizAnswers({}); }}>
                Retake Quiz
              </Button>
            </Paper>
          ) : (
            <Paper sx={{ p: 4, borderRadius: 3 }}>
              <Typography variant="h5" fontWeight={700} gutterBottom>Roommate Compatibility Quiz</Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>Answer these questions to help us find your ideal roommate match.</Typography>
              <LinearProgress variant="determinate" value={((quizStep + 1) / (QUIZ_QUESTIONS.length + 1)) * 100} sx={{ mb: 3, height: 8, borderRadius: 4 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Question {quizStep + 1} of {QUIZ_QUESTIONS.length + 1}</Typography>

              {quizStep < QUIZ_QUESTIONS.length ? (
                <Box>
                  <FormControl component="fieldset">
                    <FormLabel sx={{ fontWeight: 600, fontSize: '1.1rem', mb: 2 }}>{currentQ.label}</FormLabel>
                    <RadioGroup value={quizAnswers[currentQ.field] || ''} onChange={(e) => handleQuizAnswer(currentQ.field, e.target.value)}>
                      {currentQ.options.map(opt => (
                        <FormControlLabel key={opt.value} value={opt.value} control={<Radio />}
                          label={opt.label} sx={{ mb: 1, p: 1, borderRadius: 2, border: '1px solid', borderColor: quizAnswers[currentQ.field] === opt.value ? 'primary.main' : 'grey.200', bgcolor: quizAnswers[currentQ.field] === opt.value ? 'primary.50' : 'transparent' }} />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                    <Button variant="outlined" disabled={quizStep === 0} onClick={() => setQuizStep(s => s - 1)}>Back</Button>
                    <Button variant="contained" disabled={!quizAnswers[currentQ.field]} onClick={() => setQuizStep(s => s + 1)}>Next</Button>
                  </Stack>
                </Box>
              ) : (
                <Box>
                  <Typography fontWeight={600} sx={{ mb: 2 }}>Tell potential roommates about yourself (optional)</Typography>
                  <TextField fullWidth multiline rows={3} value={quizBio} onChange={(e) => setQuizBio(e.target.value)}
                    placeholder="e.g. I'm a Computer Science student who loves cooking..." sx={{ mb: 3 }} />
                  <Stack direction="row" spacing={2}>
                    <Button variant="outlined" onClick={() => setQuizStep(s => s - 1)}>Back</Button>
                    <Button variant="contained" onClick={handleQuizSubmit} disabled={quizLoading}>
                      {quizLoading ? 'Submitting...' : 'Complete Quiz & Find Matches'}
                    </Button>
                  </Stack>
                </Box>
              )}
            </Paper>
          )}
        </>
      )}

      {/* Tab 1: My Matches */}
      {tabValue === 1 && (
        <>
          {!quizCompleted ? (
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
              <Quiz sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">Complete the quiz first</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>Take the roommate compatibility quiz to see your matches.</Typography>
              <Button variant="contained" onClick={() => setTabValue(0)}>Take Quiz</Button>
            </Paper>
          ) : matchesLoading ? (
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}><Typography>Loading matches...</Typography></Paper>
          ) : matches.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
              <Group sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No matches yet</Typography>
              <Typography color="text.secondary">More students need to complete the quiz for matches to appear.</Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {matches.map((match) => (
                <Grid item xs={12} sm={6} md={4} key={match.id}>
                  <Card sx={{ height: '100%', borderRadius: 3, border: '2px solid', borderColor: (match.compatibility_score || 0) >= 70 ? 'success.main' : (match.compatibility_score || 0) >= 40 ? 'warning.main' : 'grey.300' }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar sx={{ bgcolor: 'primary.light' }}><Person /></Avatar>
                          <Typography fontWeight={700}>{match.user_name}</Typography>
                        </Stack>
                        <Chip label={`${match.compatibility_score || 0}% Match`} size="small"
                          color={(match.compatibility_score || 0) >= 70 ? 'success' : (match.compatibility_score || 0) >= 40 ? 'warning' : 'default'} />
                      </Stack>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={1}>
                        {[
                          { label: 'Sleep', val: match.sleep_schedule },
                          { label: 'Noise', val: match.noise_level },
                          { label: 'Clean', val: match.cleanliness },
                          { label: 'Study', val: match.study_habits },
                          { label: 'Smoking', val: match.smoking },
                          { label: 'Budget', val: match.budget_range },
                        ].filter(f => f.val).map(f => (
                          <Grid item xs={6} key={f.label}>
                            <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{f.val?.replace(/_/g, ' ')}</Typography>
                          </Grid>
                        ))}
                      </Grid>
                      {match.bio && <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>"{match.bio}"</Typography>}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Tab 2: Pairing Requests */}
      {tabValue === 2 && (
        <>
          <Paper sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100' }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><Group /></Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight={600}>How Room Pairing Works</Typography>
                <Typography variant="body2" color="text.secondary">
                  1. Select a property/room you want a roommate for &bull; 2. Post your pairing request &bull; 3. Browse other students' requests for the same property &bull; 4. Match with a compatible roommate
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {pairings.length === 0 && !loading ? (
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
              <Group sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No pairing requests available</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Be the first to post a pairing request for a specific property!</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>Post Request</Button>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {pairings.map((pairing) => (
                <Grid item xs={12} sm={6} md={4} key={pairing.id}>
                  <Card sx={{ height: '100%', borderRadius: 3, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 } }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
                            {pairing.initiator_avatar ? (
                              <img src={pairing.initiator_avatar.startsWith('http') ? pairing.initiator_avatar : `${process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8000'}${pairing.initiator_avatar}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : <Person />}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={700} variant="body2">{pairing.initiator_name || 'Student'}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(pairing.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Stack>
                        <Chip label={pairing.status} size="small" color={pairing.status === 'open' ? 'success' : pairing.status === 'matched' ? 'info' : 'default'} sx={{ textTransform: 'capitalize' }} />
                      </Stack>

                      {/* Property Info */}
                      {pairing.property_title && (
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2, bgcolor: 'grey.50' }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <School sx={{ fontSize: 18, color: 'primary.main' }} />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{pairing.property_title}</Typography>
                              {pairing.property_address && <Typography variant="caption" color="text.secondary">{pairing.property_address}</Typography>}
                            </Box>
                          </Stack>
                        </Paper>
                      )}

                      {pairing.message && <Typography variant="body2" sx={{ mb: 1.5, fontStyle: 'italic' }}>"{pairing.message}"</Typography>}
                      {pairing.preferences && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}><strong>Preferences:</strong> {pairing.preferences}</Typography>}

                      {/* Profile Summary */}
                      {pairing.profile_summary && (
                        <Box sx={{ mt: 1.5 }}>
                          <Divider sx={{ mb: 1 }} />
                          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Roommate Profile</Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {pairing.profile_summary.sleep_schedule && <Chip label={`Sleep: ${pairing.profile_summary.sleep_schedule.replace(/_/g, ' ')}`} size="small" variant="outlined" sx={{ textTransform: 'capitalize', mb: 0.5 }} />}
                            {pairing.profile_summary.cleanliness && <Chip label={`Clean: ${pairing.profile_summary.cleanliness.replace(/_/g, ' ')}`} size="small" variant="outlined" sx={{ textTransform: 'capitalize', mb: 0.5 }} />}
                            {pairing.profile_summary.noise_level && <Chip label={`Noise: ${pairing.profile_summary.noise_level.replace(/_/g, ' ')}`} size="small" variant="outlined" sx={{ textTransform: 'capitalize', mb: 0.5 }} />}
                            {pairing.profile_summary.smoking && <Chip label={`Smoking: ${pairing.profile_summary.smoking.replace(/_/g, ' ')}`} size="small" variant="outlined" sx={{ textTransform: 'capitalize', mb: 0.5 }} />}
                            {pairing.profile_summary.budget_range && <Chip label={`Budget: ${pairing.profile_summary.budget_range.replace(/_/g, ' ')}`} size="small" variant="outlined" sx={{ textTransform: 'capitalize', mb: 0.5 }} />}
                          </Stack>
                          {pairing.profile_summary.bio && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>"{pairing.profile_summary.bio}"</Typography>}
                        </Box>
                      )}

                      {pairing.status === 'open' && pairing.initiator_id !== user?.id && (
                        <Button fullWidth variant="contained" startIcon={<Handshake />} onClick={() => handleMatch(pairing.id)} sx={{ mt: 2, borderRadius: 2 }}>Match with {pairing.initiator_name?.split(' ')[0] || 'this person'}</Button>
                      )}
                      {pairing.initiator_id === user?.id && <Chip label="Your Request" size="small" color="primary" sx={{ mt: 2 }} />}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Post a Pairing Request</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            Select a specific property you'd like to find a roommate for. This helps match you with students interested in the same hostel.
          </Alert>
          <TextField fullWidth label="Property ID" sx={{ mb: 2 }} value={createForm.property_id}
            onChange={(e) => setCreateForm({ ...createForm, property_id: e.target.value })}
            helperText="Enter the ID of the property you want a roommate for (find this on the property listing or Browse Properties page)"
            required />
          <TextField fullWidth label="Message" multiline rows={3} sx={{ mb: 2 }} value={createForm.message}
            onChange={(e) => setCreateForm({ ...createForm, message: e.target.value })} placeholder="Tell potential roommates about yourself and what you're looking for..." />
          <TextField fullWidth label="Preferences" multiline rows={2} sx={{ mb: 2 }} value={createForm.preferences}
            onChange={(e) => setCreateForm({ ...createForm, preferences: e.target.value })} placeholder="e.g. Non-smoker, quiet after 10pm, early riser..." />
          {quizCompleted && quizProfile && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'success.50' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography fontWeight={600} variant="body2">Use your Roommate Profile?</Typography>
                  <Typography variant="caption" color="text.secondary">Auto-fill preferences from your quiz results</Typography>
                </Box>
                <Button size="small" variant="outlined" color="success" onClick={() => {
                  const profilePrefs = [];
                  if (quizProfile.sleep_schedule) profilePrefs.push(`Sleep: ${quizProfile.sleep_schedule.replace(/_/g, ' ')}`);
                  if (quizProfile.noise_level) profilePrefs.push(`Noise: ${quizProfile.noise_level.replace(/_/g, ' ')}`);
                  if (quizProfile.cleanliness) profilePrefs.push(`Cleanliness: ${quizProfile.cleanliness.replace(/_/g, ' ')}`);
                  if (quizProfile.smoking) profilePrefs.push(`Smoking: ${quizProfile.smoking.replace(/_/g, ' ')}`);
                  if (quizProfile.budget_range) profilePrefs.push(`Budget: ${quizProfile.budget_range.replace(/_/g, ' ')}`);
                  if (quizProfile.gender_preference) profilePrefs.push(`Gender pref: ${quizProfile.gender_preference.replace(/_/g, ' ')}`);
                  setCreateForm(prev => ({
                    ...prev,
                    preferences: profilePrefs.join(', '),
                    message: prev.message || (quizProfile.bio ? `${quizProfile.bio}` : ''),
                  }));
                }}>
                  <CheckCircle sx={{ mr: 0.5, fontSize: 16 }} /> Apply Profile
                </Button>
              </Stack>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!createForm.property_id}>Post Request</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RoomPairingPage;
